import React, {useState} from "react";
import PropTypes from "prop-types";

import {DndContext, useSensors, useSensor, MouseSensor, TouchSensor} from '@dnd-kit/core';
import {SortableContext} from '@dnd-kit/sortable';

import useMediaQuery from "@mui/material/useMediaQuery";
import theme from "../../../theme";
import useSequentialFileUploader from "../../../hooks/useSequentialFileUploader";
import {useFeedbackTools} from "../../../components/FeedbackTools";
import {FadeInOverlayBox, OverlayBox} from "../../../components/OverlayBox";
import SortableItem from "./SortableItem";
import ImageUnderUpload from "./ImageUnderUploading";
import AddImageButton from "./AddImageButton";

import {styled} from "@mui/material";
import ImageList from "@mui/material/ImageList";
import ImageListItemBar from "@mui/material/ImageListItemBar";
import ImageListItem from "@mui/material/ImageListItem";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import Skeleton from "@mui/material/Skeleton";
import CircularProgress from "@mui/material/CircularProgress";


const StyledImageListItemBar = styled(ImageListItemBar)({
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
})

const SquaredImageList = styled(ImageList)({
    overflow: 'visible',
    padding: '6px',
    '& > *': {
        aspectRatio: '1',
        overflow: 'hidden',
        minWidth: 0,
        minHeight: 0,
    }
})

/*
 * Responsive gallery with small squared image and drag and drop support for ordering items.
 */
const Gallery = (props) => {
    const {photos, onDeletePhoto, onCreatePhoto, onPositionChange = async f => f} = props;
    const {showErrorNotification} = useFeedbackTools();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    /*
     * Keep track of the string id of the photo currently uploading, if any, to show a
     * circular progress and to disable dragging.
     */
    const [updatingPhotoId, setUpdatingPhotoId] = useState(null);

    const onImagesSelection = async (event) => {
        event.preventDefault();
        appendFiles(event.target.files);
        event.target.value = null;  // Clear the input to select the same file another times (In case of a deletion)
    }

    const handleCreatePhoto = async (file) => {
        try {
            await onCreatePhoto(file);
        } catch (e) {
            showErrorNotification(e);
        }
    }

    const {uploadingFile, remainingFiles, appendFiles} = useSequentialFileUploader(handleCreatePhoto);

    const handleDeletePhoto = async (file) => {
        try {
            await onDeletePhoto(file);
        } catch (e) {
            showErrorNotification(e);
        }
    }

    const handleDragEnd = async (event) => {
        const {active, over} = event;
        if (over) {
            const id_list = photos.map(p => p.id.toString());
            const oldIndex = id_list.indexOf(active.id);
            const newIndex = id_list.indexOf(over.id);
            if (oldIndex !== newIndex) {
                setUpdatingPhotoId(active.id)
                try {
                    await onPositionChange(active.id, oldIndex, newIndex);
                } catch (e) {
                    showErrorNotification(e);
                }
                setUpdatingPhotoId(null)
            }
        }
    }

    const sensors = useSensors(
        useSensor(MouseSensor, {
            // Require the mouse to move by 10 pixels before activating (so the click on the actions still works)
            activationConstraint: {
                distance: 10,
            }
        }),
        useSensor(TouchSensor, {
            // Press delay of 250ms, with tolerance of 5px of movement
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        }),
    );

    return (
        <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
            <SortableContext items={photos.map(i => i.id.toString())}>
                <SquaredImageList cols={isMobile ? 3 : 5} gap={6}>
                    <AddImageButton onImagesSelection={onImagesSelection}/>

                    {
                        photos.map((p) =>
                            <SortableItem key={p.id.toString()}
                                          id={p.id.toString()}
                                          disabled={!!updatingPhotoId}>
                                <PhotoItem photo={p}
                                           onDelete={handleDeletePhoto}
                                           isUpdating={p.id.toString() === updatingPhotoId}/>
                            </SortableItem>
                        )
                    }

                    {
                        uploadingFile && [uploadingFile, ...remainingFiles].map((u, i) => (
                            <ImageUnderUpload key={i}
                                              file={u.name}
                                              isUploading={i === 0}/>
                        ))
                    }
                </SquaredImageList>
            </SortableContext>
        </DndContext>
    );
}

    const PhotoItem = (props) => {
        const {photo, onDelete, isUpdating} = props;
        const [isDeleting, setIsDeleting] = useState(false);
        const [loaded, setLoaded] = useState(false);

        const handleDelete = async () => {
            setIsDeleting(true);
            await onDelete(photo.id);
        }

        const handleOnLoad = () => {
            setLoaded(true)
        }

        return <ImageListItem style={{height: '100%'}}>
            <img src={`${photo.src}`}
                 alt={photo.alt}
                 loading="lazy"
                 onLoad={handleOnLoad}/>

            {!loaded && <Skeleton variant="rectangular" width="100%" height="100%"/>}

            {
                loaded && !isUpdating &&
                <FadeInOverlayBox>
                    <StyledImageListItemBar
                        position='top'
                        actionIcon={
                            <IconButton onClick={handleDelete}
                                        sx={{color: 'rgba(255, 255, 255)'}}
                                        disabled={isDeleting}>
                                <DeleteIcon/>
                            </IconButton>
                        }
                    />
                </FadeInOverlayBox>
            }
            {
                loaded && isUpdating &&
                <OverlayBox sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(0, 0, 0, 0.6)'
                }}>
                    <CircularProgress color="primary"/>
                </OverlayBox>
            }
        </ImageListItem>
    }

    Gallery.propTypes = {
        photos: PropTypes.arrayOf(PropTypes.object).isRequired,   // Each photo object should have: id, src (url string), alt (string).
        onDeletePhoto: PropTypes.func,  // Called when uploading a new photo, a file is passed as argument
        onCreatePhoto: PropTypes.func,  // Called when deleting a photo, the id of the photo is passed as argument
        onPositionChange: PropTypes.func,  // Called when the position of a photo is changed, the string id of the photo, the old index and the new index are passed as argument
    };

    export default Gallery;